// BUG-2370: readable "what will be saved" review for upsertB2CCustomer.
//
// Built from the exact customerList payload buildCustomerListPayload produced
// (not from the form), so the cashier sees what the server will receive: state
// names instead of ids, which address ids are existing rows and which are new,
// B2B on/off and Same as Invoicing. Pure functions only -- the popup lives in
// components/CustomerReview/CustomerReviewHost.js and the bridge in
// stores/useCustomerReviewStore.js.

// GSTIN characters 1-2 = state code (GST state code list, CBIC).
export const GST_STATE_CODES = {
  "01": "Jammu and Kashmir",
  "02": "Himachal Pradesh",
  "03": "Punjab",
  "04": "Chandigarh",
  "05": "Uttarakhand",
  "06": "Haryana",
  "07": "Delhi",
  "08": "Rajasthan",
  "09": "Uttar Pradesh",
  10: "Bihar",
  11: "Sikkim",
  12: "Arunachal Pradesh",
  13: "Nagaland",
  14: "Manipur",
  15: "Mizoram",
  16: "Tripura",
  17: "Meghalaya",
  18: "Assam",
  19: "West Bengal",
  20: "Jharkhand",
  21: "Odisha",
  22: "Chhattisgarh",
  23: "Madhya Pradesh",
  24: "Gujarat",
  25: "Daman and Diu",
  26: "Dadra and Nagar Haveli and Daman and Diu",
  27: "Maharashtra",
  28: "Andhra Pradesh",
  29: "Karnataka",
  30: "Goa",
  31: "Lakshadweep",
  32: "Kerala",
  33: "Tamil Nadu",
  34: "Puducherry",
  35: "Andaman and Nicobar Islands",
  36: "Telangana",
  37: "Andhra Pradesh",
  38: "Ladakh",
  97: "Other Territory",
};

const STATE_ALIASES = {
  orissa: "odisha",
  pondicherry: "puducherry",
  newdelhi: "delhi",
  nctofdelhi: "delhi",
  uttaranchal: "uttarakhand",
};

const normStateName = (name) => {
  const n = (name ?? "")
    .toString()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z]/g, "");
  return STATE_ALIASES[n] || n;
};

export const gstinStateName = (gstin) => {
  const code = (gstin || "").toString().trim().slice(0, 2);
  return /^\d{2}$/.test(code) ? GST_STATE_CODES[code] || null : null;
};

export const sameState = (a, b) => !!a && !!b && normStateName(a) === normStateName(b);

// A state / country value may be a RecordID or a name. Returns the readable name,
// or null when it is neither (that is when buildCustomerListPayload silently
// falls back to the store's own region -- BUG-2555).
export const lookupName = (value, rows) => {
  if (!value) return null;
  const list = Array.isArray(rows) ? rows : [];
  const byId = list.find((r) => r.RecordID === value);
  if (byId) return byId.Name || null;
  const byName = list.find((r) => r.Name?.toLowerCase() === value.toString().toLowerCase());
  return byName ? byName.Name : null;
};

const addressText = (a) => {
  if (!a) return "";
  const line1 = (a.line1 || "").trim();
  const line2 = (a.line2 || "").trim();
  return [line1, line2 && line2 !== line1 ? line2 : "", (a.city || "").trim(), (a.postalcode || a.pincode || "").toString().trim()].filter(Boolean).join(", ");
};

const yesNo = (v) => (v ? "Yes" : "No");
// B2B_LABEL / B2C_LABEL are declared further down; only read when called.
const customerType = (isB2B) => (isB2B ? B2B_LABEL : B2C_LABEL);

// Readable view of a server address row (getB2BCustomerList) or a cached snapshot.
const readableRow = (row, statesData, countriesData) => {
  if (!row) return null;
  return {
    address: addressText(row),
    state: row.regionName || lookupName(row.regionId || row.state, statesData) || row.state || "",
    country: row.countryName || lookupName(row.countryId || row.country, countriesData) || row.country || "",
    addressId: row.s_customer_address_id || null,
    addressIdStatus: row.s_customer_address_id ? "Existing" : "",
  };
};

// "After": what this save sends. payloadEntry = customerList[0] from buildCustomerListPayload.
export const describeSubmitted = ({ data, payloadEntry, isB2BEnabled, switchToggle, statesData, countriesData, storeRegionName, knownAddressIds }) => {
  const known = new Set((knownAddressIds || []).filter(Boolean));
  const after = {
    name: (data?.name || data?.customerName || data?.editName || "").toString().trim(),
    mobile: (data?.mobile || data?.mobileno || data?.editMobile || "").toString().trim(),
    email: (data?.email || data?.editEmail || "").toString().trim(),
    b2b: customerType(isB2BEnabled),
  };
  if (!isB2BEnabled || !payloadEntry) return after;
  const [inv, del] = payloadEntry.customerAddress || [];
  const fin = payloadEntry.finLinCustomer || [];
  const side = (addr) => {
    if (!addr) return null;
    const stateName = lookupName(addr.csRegionId, statesData);
    return {
      address: addressText(addr),
      state: stateName || "",
      stateRecognised: !!stateName,
      country: lookupName(addr.csCountryId, countriesData) || "",
      addressId: addr.address_id || null,
      addressIdStatus: addr.address_id && known.has(addr.address_id) ? "Existing" : "New",
    };
  };
  return {
    ...after,
    company: payloadEntry.name || "",
    legalName: payloadEntry.legalName || "",
    gstin: (fin[0]?.gstNo || payloadEntry.gstNo || "").toString().toUpperCase(),
    gstinState: gstinStateName(fin[0]?.gstNo || payloadEntry.gstNo) || "",
    b2bCustomerId: payloadEntry.sCustomerId || null,
    sameAsInvoicing: yesNo(switchToggle),
    billing: side(inv),
    shipping: side(del),
    storeRegionName: storeRegionName || "",
  };
};

// GSTIN of a getB2BCustomerList customer. Its gstNo is s_customer.taxid, which is
// NULL for every KWC B2B customer (live, 30 days to 2026-10-09); the GSTIN the POS
// saved lives in fin_lin_customer and comes back as localisation[].gstNo.
export const serverGstin = (c) => {
  if (!c) return "";
  const fromLoc = (Array.isArray(c.localisation) ? c.localisation : []).find((l) => l?.gstNo)?.gstNo;
  return (c.gstNo || c.taxId || fromLoc || "").toString().trim().toUpperCase();
};

// "Before": what the server / cart held before this save.
export const describeExisting = ({ customer, wasB2B, serverB2BCustomer, statesData, countriesData, billingId, shippingId }) => {
  if (!customer && !serverB2BCustomer) return null;
  const before = {
    name: (customer?.name || "").toString().trim(),
    mobile: (customer?.mobileNo || "").toString().trim(),
    email: (customer?.email || "").toString().trim(),
    b2b: customerType(wasB2B),
  };
  const ids = customer?.customerAddressIds || null;
  const rows = [...(serverB2BCustomer?.invoiceAddresses || []), ...(serverB2BCustomer?.deliveryAddresses || [])];
  const findRow = (id) => (id ? rows.find((r) => r.s_customer_address_id === id) : null);
  const billingRow = findRow(billingId) || (ids?.invoiceSnapshot ? { ...ids.invoiceSnapshot, s_customer_address_id: ids.billingId } : null);
  const shippingRow = findRow(shippingId) || (ids?.deliverySnapshot ? { ...ids.deliverySnapshot, s_customer_address_id: ids.shippingId } : null);
  const gstin = serverGstin(serverB2BCustomer);
  return {
    ...before,
    company: serverB2BCustomer?.name || serverB2BCustomer?.customerName || "",
    gstin,
    gstinState: gstinStateName(gstin) || "",
    billing: readableRow(billingRow, statesData, countriesData),
    shipping: readableRow(shippingRow, statesData, countriesData),
    sameAsInvoicing: ids ? yesNo(!!ids.billingId && ids.billingId === ids.shippingId) : "",
  };
};

// ---- Every field the cashier filled in (B2C and B2B) ------------------------

const normKey = (k) => (k ?? "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");

// Shown in the top rows already, or internal to the form -- not repeated below.
const SKIP_DETAIL_KEYS = new Set([
  "name",
  "customername",
  "editname",
  "mobile",
  "mobileno",
  "editmobile",
  "email",
  "editemail",
  "editpincode",
  "type",
  "customerdocumenturls",
  "pancardurl",
  "passporturl",
  "scustomer",
  "customeraddressids",
]);

const humanize = (key) =>
  (key ?? "")
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

const isDayjsLike = (v) => v && typeof v === "object" && typeof v.format === "function" && typeof v.isValid === "function";

// Label of a select option. Options come as { RecordID, Name } (states, countries,
// loyalty levels as the forms map them) or { value, label } (static options).
const optionLabel = (value, options) => {
  if (!Array.isArray(options) || !options.length) return null;
  const v = String(value);
  const hit = options.find((o) => o && (String(o.RecordID ?? o.value ?? o.id ?? "") === v || String(o.Name ?? o.label ?? o.name ?? "").toLowerCase() === v.toLowerCase()));
  return hit ? String(hit.Name ?? hit.label ?? hit.name ?? v) : null;
};

// Readable text for one value. State / country ids become names; a select id becomes
// its option label; a JSON string (KWC keeps "Loyalty Level" as a JSON object
// string on some customers) is read as that object; dates DD-MM-YYYY.
export const formatDetailValue = (key, value, { statesData, countriesData, options } = {}) => {
  const ref = { statesData, countriesData, options };
  if (value === undefined || value === null || value === "") return "";
  if (isDayjsLike(value)) return value.isValid() ? value.format("DD-MM-YYYY") : "";
  if (value instanceof Date) return isNaN(value.getTime()) ? "" : value.toISOString().slice(0, 10).split("-").reverse().join("-");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((v) => formatDetailValue(key, v, ref)).filter(Boolean).join(", ");
  if (typeof value === "object") {
    if (value.name) return String(value.name);
    if (value.Name) return String(value.Name);
    if (value.label) return String(value.label);
    // e.g. {"cwrLoyaltyLevelId":"9DE5..."} or every value null (KWC live).
    const vals = Object.values(value).filter((v) => v !== null && v !== undefined && v !== "");
    if (!vals.length) return "";
    for (const v of vals) {
      const label = typeof v !== "object" ? optionLabel(v, options) : null;
      if (label) return label;
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  const text = String(value).trim();
  if (/^[[{]/.test(text)) {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === "object") return formatDetailValue(key, parsed, ref);
    } catch (e) {
      // not JSON: shown as typed
    }
  }
  const fromOptions = optionLabel(text, options);
  if (fromOptions) return fromOptions;
  const k = normKey(key);
  if (k.includes("state") || k.includes("region")) return lookupName(text, statesData) || text;
  if (k.includes("country")) return lookupName(text, countriesData) || text;
  // "2026-10-09", "2026-10-09T..." and the server's "2026-10-09 00:00:00" read the same.
  if (/^\d{4}-\d{2}-\d{2}([T ]|$)/.test(text)) return text.slice(0, 10).split("-").reverse().join("-");
  return text;
};

const CITY_ALT = "\u0000cityline2";

// Group fields the form fills by itself (select defaults).
const DEFAULTED_GROUP_KEYS = /^(country|state|region)$/;

// A form field whose saved value sits under another name on the customer record.
const BEFORE_ALIASES = {
  loyaltybalancepoints: ["retlloyaltybalance", "loyaltybalance"],
};

// Keys of the saved record / the upsert reply that the edit form carries along.
// Never typed by the cashier; ids here have no readable name.
const RECORD_COPY_KEYS = new Set([
  "cwrcustomerid",
  "spricelistid",
  "code",
  "b2cregisteredstoreid",
  "b2bdeliverystateid",
  "retlloyaltybalance",
  "program",
  "status",
  "message",
  "customerchangelog",
]);

const parseMaybeJson = (v) => {
  if (typeof v !== "string") return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return v;
  }
};

// Values the saved customer held, keyed by normKey. Covers the customerSearch shape
// (top-level fields, customerAddress, loyaltyLevel, metaData [{key,value}]) and the
// edit form's own mapping (street = line1, city = line2, state = region).
const oldCustomerValues = (customer) => {
  const out = new Map();
  if (!customer || typeof customer !== "object") return out;
  const put = (k, v) => {
    if (v === undefined || v === null || v === "") return;
    if (!out.has(normKey(k))) out.set(normKey(k), v);
  };
  Object.entries(customer).forEach(([k, v]) => {
    if (v === null || v === undefined || typeof v === "function") return;
    if (typeof v !== "object" || isDayjsLike(v)) put(k, v);
  });
  const addr = customer.customerAddress || {};
  put("street", addr.line1);
  put("line1", addr.line1);
  // The legacy edit form fills "city" from line2 (useCustomerOperations); the config
  // drawer from customerAddress.city. Both are kept; describeFormDetails picks the one
  // the form showed (CITY_ALT).
  put("city", addr.city || addr.line2);
  if (addr.city && addr.line2) out.set(CITY_ALT, addr.line2);
  put("line2", addr.line2);
  put("state", addr.region);
  put("region", addr.region);
  put("country", addr.country);
  put("mobile", customer.mobileNo);
  put("taxId", customer.taxId);
  put("loyaltyLevel", customer.loyaltyLevel?.name);
  put("program", customer.loyaltyLevel?.name);
  (Array.isArray(customer.metaData) ? customer.metaData : []).forEach((m) => {
    if (!m?.key) return;
    const parsed = parseMaybeJson(m.value);
    if (/^group_\d+$/i.test(m.key) && parsed && typeof parsed === "object") {
      Object.entries(parsed).forEach(([sub, sv]) => put(`${m.key}.${sub}`, sv));
    } else {
      put(m.key, m.value);
    }
  });
  return out;
};

// One row per filled field: { key, label, before, after }. fields / groups are the
// customer config's retailCustomerFields / retailCustomerGroups (labels come from
// there); oldCustomer is the saved customer on an update (null on create).
// apiDataSources = the config's apiDataSources; loyaltyLevels = localStorage
// loyaltyData.loyaltyLevels. Together they give a select field the same option list
// the form shows (fetchOptions in useCustomerFormData / EditCustomerDrawer), so an
// id is shown as its label.
const fieldOptions = (field, { statesData, countriesData, apiDataSources, loyaltyLevels }) => {
  const ds = field?.dataSource;
  if (!ds) return null;
  if (ds.type === "static") {
    if (Array.isArray(ds.staticOptions) && ds.staticOptions.length) return ds.staticOptions;
    // KWC keeps the list as a JSON object in defaultValue ({"COM":"Composite"}), as the drawer reads it.
    const parsed = parseMaybeJson(field.defaultValue);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return Object.entries(parsed).map(([value, label]) => ({ value, label }));
    return null;
  }
  if (ds.type !== "api") return null;
  const api = (Array.isArray(apiDataSources) ? apiDataSources : []).find((a) => a.id === ds.apiSourceId);
  if (api?.name === "getStates") return statesData;
  if (api?.name === "getCountries") return countriesData;
  if (api?.name === "getLoyaltyData") return (Array.isArray(loyaltyLevels) ? loyaltyLevels : []).map((l) => ({ RecordID: l.cwr_loyalty_level_id, Name: l.name }));
  return null;
};

export const describeFormDetails = ({ data, fields, groups, statesData, countriesData, oldCustomer, apiDataSources, loyaltyLevels }) => {
  const fieldList = Array.isArray(fields) ? fields : [];
  const groupList = Array.isArray(groups) ? groups : [];
  const optionSources = { statesData, countriesData, apiDataSources, loyaltyLevels };
  const old = oldCustomerValues(oldCustomer);
  const rows = [];
  const seen = new Set();
  const add = (key, label, afterRaw, beforeRaw, field) => {
    const ref = { statesData, countriesData, options: fieldOptions(field, optionSources) };
    const after = formatDetailValue(key, afterRaw, ref);
    const before = formatDetailValue(key, beforeRaw, ref);
    if (!after && !before) return;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ key: `detail.${key}`, label, before, after });
  };
  // "<field>=<readable value>" of every group field, to spot a top-level repeat.
  const groupValues = new Set();
  const nestedGroupIds = new Set();
  Object.entries(data && typeof data === "object" ? data : {}).forEach(([key, value]) => {
    if (!groupList.some((g) => normKey(g.id) === normKey(key))) return;
    const parsed = parseMaybeJson(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;
    nestedGroupIds.add(normKey(key));
    Object.entries(parsed).forEach(([sub, sv]) => groupValues.add(`${normKey(sub)}=${formatDetailValue(sub, sv, { statesData, countriesData })}`));
  });
  Object.entries(data && typeof data === "object" ? data : {}).forEach(([key, value]) => {
    if (typeof value === "function" || SKIP_DETAIL_KEYS.has(normKey(key))) return;
    const group = groupList.find((g) => normKey(g.id) === normKey(key));
    const parsed = group ? parseMaybeJson(value) : value;
    if (group && parsed && typeof parsed === "object" && !Array.isArray(parsed) && !isDayjsLike(parsed)) {
      const groupName = (group.name || humanize(key)).trim();
      // A group the cashier never opened still carries the form's own defaults
      // (KWC: Country / State on the Business tab of a B2C customer). With nothing
      // else in the group and nothing saved before, that is not a change.
      const filled = Object.entries(parsed).filter(([, sv]) => formatDetailValue("", sv) !== "");
      const hadBefore = Object.keys(parsed).some((sub) => old.has(normKey(`${key}.${sub}`)));
      if (!hadBefore && filled.every(([sub]) => DEFAULTED_GROUP_KEYS.test(normKey(sub)))) return;
      Object.entries(parsed).forEach(([sub, sv]) => {
        const subField = fieldList.find((f) => f.groupId === group.id && normKey(f.name) === normKey(sub));
        add(`${normKey(key)}.${normKey(sub)}`, `${groupName} - ${subField?.name?.trim() || humanize(sub)}`, sv, old.get(normKey(`${key}.${sub}`)), subField);
      });
      return;
    }
    // A field of a group the form sends as its own object (group_<id>: {...}) is shown
    // under that group; a top-level key of the same name is not that field.
    const named = fieldList.filter((f) => normKey(f.name) === normKey(key) || normKey(f.id) === normKey(key));
    const field = named.find((f) => !f.groupId) || named.find((f) => !nestedGroupIds.has(normKey(f.groupId)));
    // The drawers open with a hidden "state" / "country" taken from the store's own
    // address (showAddNewCustomerFields, the edit form). The cashier never sees it and
    // the address is saved from the drawer's State / Country field. When the drawer shows
    // that field only inside groups and the form sends a group as its own object, the
    // top-level copy is not a drawer field and is not listed (a store whose region and
    // address region differ showed a second, different State).
    if (DEFAULTED_GROUP_KEYS.test(normKey(key)) && named.length && named.every((f) => f.groupId) && named.some((f) => nestedGroupIds.has(normKey(f.groupId)))) return;
    // The edit form also carries copies of the saved record that no field shows
    // (cwrWallet, customerAddress, csBunit): not entered by the cashier, not listed.
    if (!field && value && typeof value === "object" && !Array.isArray(value) && !isDayjsLike(value) && !(value instanceof Date)) return;
    let before = old.get(normKey(key));
    if (before === undefined) before = (BEFORE_ALIASES[normKey(key)] || []).map((k) => old.get(k)).find((v) => v !== undefined);
    // With a configured form, a key no field shows is mostly a copy of the saved record
    // or of the last server reply (Code, Cwr Customer Id, Credit Limit, Status, Message,
    // a second City / State). It is listed only when it carries something new: a value
    // that differs from the saved one (the drawer's own switches, e.g. NRI Billing), or
    // on a create a value that is neither a record key nor a repeat of a group field.
    if (!field && fieldList.length) {
      const text = formatDetailValue(key, value, { statesData, countriesData });
      if (RECORD_COPY_KEYS.has(normKey(key))) return;
      // Only what the cashier can enter or change is shown: a form with no State / Country
      // field does not list the store's hidden one either.
      if (DEFAULTED_GROUP_KEYS.test(normKey(key))) return;
      if (oldCustomer ? text === formatDetailValue(key, before, { statesData, countriesData }) : groupValues.has(`${normKey(key)}=${text}`)) return;
    }
    if (normKey(key) === "city" && old.has(CITY_ALT) && formatDetailValue(key, value) === formatDetailValue(key, old.get(CITY_ALT))) before = old.get(CITY_ALT);
    add(normKey(key), field?.name?.trim() || humanize(key), value, before, field);
  });
  return rows;
};

// ---- Top rows ----------------------------------------------------------------

// Headings match the customer drawer. Nothing here says "sent": the popup is shown
// before the save.
export const SECTION_MAIN = "Customer Details";
export const SECTION_B2B = "B2B Details";
export const SECTION_INVOICING = "Invoicing Address";
export const SECTION_DELIVERY = "Delivery Address";
// Form fields of a client with no customer form config sit with the customer's own rows.
export const SECTION_DETAILS = SECTION_MAIN;
export const SECTION_OTHER = "Other details";

const ROWS = [
  ["name", "Customer name", SECTION_MAIN],
  ["mobile", "Mobile", SECTION_MAIN],
  ["email", "Email", SECTION_MAIN],
  ["b2b", "Customer type", SECTION_MAIN],
  ["company", "Company name", SECTION_B2B],
  ["legalName", "Legal name", SECTION_B2B],
  ["gstin", "GSTIN", SECTION_B2B],
  ["gstinState", "GSTIN state", SECTION_B2B],
  ["sameAsInvoicing", "Same as Invoicing", SECTION_B2B],
  ["billing.address", "Invoicing address", SECTION_INVOICING],
  ["billing.state", "Invoicing state", SECTION_INVOICING],
  ["billing.country", "Invoicing country", SECTION_INVOICING],
  ["billing.addressIdStatus", "Invoicing address record", SECTION_INVOICING],
  ["shipping.address", "Delivery address", SECTION_DELIVERY],
  ["shipping.state", "Delivery state", SECTION_DELIVERY],
  ["shipping.country", "Delivery country", SECTION_DELIVERY],
  ["shipping.addressIdStatus", "Delivery address record", SECTION_DELIVERY],
];
// Always listed, blank when empty.
const MAIN_ROW_KEYS = new Set(["name", "mobile", "email", "b2b"]);

const pick = (obj, path) => path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

export const B2B_LABEL = "B2B (business, GST bill)";
export const B2C_LABEL = "B2C (retail customer)";

export const buildReviewWarnings = (after, before) => {
  const warnings = [];
  if (before && before.b2b === B2B_LABEL && after.b2b === B2C_LABEL) warnings.push("B2B billing will be turned OFF for this customer. The bill will be a B2C bill.");
  if (after.b2b !== B2B_LABEL) return warnings;
  if (!after.gstin) warnings.push("B2B is ON but no GSTIN is entered. E-invoice needs a GSTIN.");
  if (!after.billing?.address) warnings.push("Invoicing address is empty. E-invoice needs the buyer's full address.");
  [
    ["Invoicing", after.billing],
    ["Delivery", after.shipping],
  ].forEach(([label, side]) => {
    if (side && !side.stateRecognised) {
      warnings.push(`${label} state was not recognised${after.storeRegionName ? `. The store's state (${after.storeRegionName}) will be saved instead` : ""}. Pick the state from the list.`);
    }
  });
  if (after.gstinState && after.billing?.state && !sameState(after.gstinState, after.billing.state)) {
    warnings.push(`GSTIN belongs to ${after.gstinState} but the invoicing state is ${after.billing.state}. E-invoice (IRN) will be rejected for a state mismatch.`);
  }
  if (after.sameAsInvoicing === "No" && !after.shipping?.address) warnings.push("Same as Invoicing is OFF and the delivery address is empty.");
  return warnings;
};

// ---- Popup layout: the customer drawer, read-only ------------------------------

// Top rows a form field of the same meaning reads its Before / After from (these
// keys are left out of describeFormDetails).
const TOP_ROW_ALIASES = { name: "name", customername: "name", editname: "name", mobile: "mobile", mobileno: "mobile", editmobile: "mobile", email: "email", editemail: "email" };

const findKey = (obj, ...names) => Object.keys(obj).find((k) => names.some((n) => n && normKey(k) === normKey(n)));

// The drawer's own structure for this save: one entry per visible tab, its groups in
// order and every visible field of each group -- with the value the form holds, blank
// when nothing is entered. Same visibility rules as AddCustomerDrawer /
// EditCustomerDrawer (buildGroupedFields + the field filter): the Business tab only
// with B2B on; on the Contact tab PAN / NRI / Credit Limit fields only with their
// toggle on; a collapsible group ("Same as Invoicing") shows no fields while it is on.
// Returns [] when the client has no customer form config (legacy form).
export const describeFormLayout = ({
  data,
  tabs,
  groups,
  fields,
  statesData,
  countriesData,
  apiDataSources,
  loyaltyLevels,
  isB2BEnabled,
  isPanCustomer,
  isNriBilling,
  isCreditSale,
  switchToggle,
}) => {
  const src = data && typeof data === "object" ? data : {};
  const fieldList = (Array.isArray(fields) ? fields : []).filter((f) => f && f.visible && f.name);
  const groupList = (Array.isArray(groups) ? groups : []).filter((g) => g && g.visible);
  const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);
  const optionSources = { statesData, countriesData, apiDataSources, loyaltyLevels };
  const out = [];
  (Array.isArray(tabs) ? tabs : [])
    .filter((t) => t && t.visible)
    .sort(byOrder)
    .forEach((tab) => {
      const tabName = (tab.name || "").trim();
      const isBusiness = tabName.toLowerCase().includes("business");
      if (isBusiness && !isB2BEnabled) return;
      const isContact = tabName.toLowerCase() === "contact";
      const shown = (f) => {
        if (f.tabId !== tab.id) return false;
        if (!isContact) return true;
        const fn = f.name.toLowerCase().replace(/\s+/g, "");
        if (fn.includes("pan") && !isPanCustomer) return false;
        if ((fn.startsWith("passport") || fn.startsWith("visa") || fn.startsWith("immigration")) && !isNriBilling) return false;
        if (fn.startsWith("credit") && !isCreditSale) return false;
        return true;
      };
      const describeField = (f, group) => {
        const ref = { statesData, countriesData, options: fieldOptions(f, optionSources) };
        let raw;
        let key;
        const groupKey = group ? findKey(src, group.id) : null;
        const groupData = groupKey ? parseMaybeJson(src[groupKey]) : null;
        if (group && groupData && typeof groupData === "object" && !Array.isArray(groupData)) {
          const sub = findKey(groupData, f.name, f.id);
          raw = sub ? groupData[sub] : undefined;
          key = `detail.${normKey(group.id)}.${normKey(sub || f.name)}`;
        } else {
          const top = findKey(src, f.name, f.id);
          raw = top ? src[top] : undefined;
          key = `detail.${normKey(top || f.name)}`;
        }
        return { key, id: f.id, label: f.name.trim(), value: formatDetailValue(f.name, raw, ref) };
      };
      const tabGroups = [];
      const ungrouped = fieldList.filter((f) => shown(f) && !f.groupId).sort(byOrder);
      if (ungrouped.length) tabGroups.push({ key: `${tab.id}:`, title: "", note: "", fields: ungrouped.map((f) => describeField(f, null)) });
      groupList
        .filter((g) => g.tabId === tab.id)
        .sort(byOrder)
        .forEach((g) => {
          const inGroup = fieldList.filter((f) => shown(f) && f.groupId === g.id).sort(byOrder);
          if (!inGroup.length) return;
          const collapsed = !!g.collapsible && !!switchToggle;
          tabGroups.push({
            key: `${tab.id}:${g.id}`,
            groupKey: normKey(g.id),
            collapsed,
            title: (g.name || "").trim(),
            note: g.collapsible ? `${(g.description || "Same as Invoicing").trim()}: ${yesNo(switchToggle)}` : "",
            fields: collapsed ? [] : inGroup.map((f) => describeField(f, g)),
          });
        });
      if (tabGroups.length) out.push({ key: `tab:${tab.id}`, title: tabName, isBusiness, groups: tabGroups });
    });
  return out;
};

// No customer form config: the rows themselves, grouped by their section.
const sectionsFromRows = (rows) => {
  const out = [];
  rows.forEach((r) => {
    const title = r.section || SECTION_MAIN;
    let section = out.find((s) => s.title === title);
    if (!section) {
      section = { key: `section:${title}`, title, groups: [{ key: `section:${title}:0`, title: "", note: "", was: "", fields: [] }] };
      out.push(section);
    }
    section.groups[0].fields.push({ key: r.key, label: r.label, value: r.after, before: r.before, changed: r.changed });
  });
  return out;
};

// Puts each layout field's Before / After / changed from the review rows (the same
// rows the change log is built from), so the popup and the log never disagree.
const sectionsFromLayout = (layout, rows, { mode, wasB2B }) => {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const used = new Set();
  const sections = layout.map((tab) => ({
    ...tab,
    groups: tab.groups.map((g) => {
      // The saved address of a B2B customer is known as one text (server row), not per field.
      const side = !tab.isBusiness ? null : /invoic/i.test(g.title) ? "billing" : /deliver/i.test(g.title) ? "shipping" : null;
      let was = "";
      if (mode === "update" && side && [`${side}.address`, `${side}.state`].some((k) => byKey.get(k)?.changed)) {
        was = [byKey.get(`${side}.address`)?.before, byKey.get(`${side}.state`)?.before].filter(Boolean).join(", ");
      }
      // An address picked from the company's saved list is not typed into the form:
      // show what the save sends instead of a block of blank fields.
      if (side && g.fields.length && g.fields.every((f) => !f.value) && byKey.get(`${side}.address`)?.after) {
        const sent = [`${side}.address`, `${side}.state`, `${side}.country`].map((k) => byKey.get(k)).filter(Boolean);
        return { ...g, was, fields: sent.map((r) => ({ key: r.key, label: r.label, value: r.after, before: r.before, changed: r.changed })) };
      }
      return {
        ...g,
        was,
        fields: g.fields.map((f) => {
          const alias = TOP_ROW_ALIASES[f.key.replace(/^detail\./, "")];
          const row = byKey.get(f.key) || (alias ? byKey.get(alias) : null);
          if (!row) return { ...f, before: "", changed: false };
          used.add(row.key);
          // A B2B field with no saved per-field value on a customer that already was B2B:
          // the old value is not known, so it is not marked as a change.
          const beforeUnknown = mode === "update" && tab.isBusiness && wasB2B && !row.before;
          return { ...f, value: row.after, before: row.before, changed: row.changed && !beforeUnknown };
        }),
      };
    }),
  }));
  // A form config with no Business tab: the B2B rows are still shown.
  if (!layout.some((tab) => tab.isBusiness)) {
    sections.push(...sectionsFromRows(rows.filter((r) => [SECTION_B2B, SECTION_INVOICING, SECTION_DELIVERY].includes(r.section))));
  }
  // A group the drawer shows collapsed ("Same as Invoicing" on) still carries the copied
  // fields in the form: the popup shows the group's note, as the drawer does, not the copies.
  const collapsedPrefixes = layout.flatMap((tab) => tab.groups.filter((g) => g.collapsed && g.groupKey).map((g) => `detail.${g.groupKey}.`));
  // A value that will be saved but that no drawer field shows (a drawer switch).
  const rest = rows.filter(
    (r) => r.section === SECTION_DETAILS && r.key.startsWith("detail.") && !used.has(r.key) && r.changed && !collapsedPrefixes.some((p) => r.key.startsWith(p)),
  );
  if (rest.length) {
    sections.push({
      key: "section:other",
      title: SECTION_OTHER,
      groups: [{ key: "section:other:0", title: "", note: "", was: "", fields: rest.map((r) => ({ key: r.key, label: r.label, value: r.after, before: r.before, changed: r.changed })) }],
    });
  }
  return sections;
};

// mode: "create" | "update". details = describeFormDetails(...) rows; layout =
// describeFormLayout(...) (optional). Returns { mode, rows, sections, warnings, before,
// after }. rows feed the change log; sections are what the popup draws.
export const buildCustomerChangeReview = ({ mode, before, after, details, layout }) => {
  const isB2BSave = after?.b2b === B2B_LABEL || before?.b2b === B2B_LABEL;
  const rows = ROWS.map(([path, label, section]) => {
    const a = pick(after, path);
    const b = before ? pick(before, path) : undefined;
    // Every field of a section is listed, blank when empty; the B2B sections only on a B2B save.
    const always = MAIN_ROW_KEYS.has(path) || isB2BSave;
    if (!always && (a === undefined || a === null || a === "") && (b === undefined || b === null || b === "")) return null;
    const afterVal = a ?? "";
    const beforeVal = mode === "create" && !before ? "" : b ?? "";
    return { key: path, label, section, before: beforeVal, after: afterVal, changed: mode === "create" ? !!afterVal : String(beforeVal) !== String(afterVal) };
  }).filter(Boolean);
  (Array.isArray(details) ? details : []).forEach((d) => {
    const beforeVal = mode === "create" ? "" : d.before ?? "";
    const afterVal = d.after ?? "";
    rows.push({ key: d.key, label: d.label, section: SECTION_DETAILS, before: beforeVal, after: afterVal, changed: mode === "create" ? !!afterVal : String(beforeVal) !== String(afterVal) });
  });
  const sections = Array.isArray(layout) && layout.length ? sectionsFromLayout(layout, rows, { mode, wasB2B: before?.b2b === B2B_LABEL }) : sectionsFromRows(rows);
  return { mode, rows, sections, warnings: buildReviewWarnings(after, before), before, after };
};

// Key of the from / to log sent in upsertB2CCustomer and posOrderProcessor1 metaData.
export const CHANGE_LOG_META_KEY = "Customer Change Log";
// b2c_customer_meta.value and s_order_meta.value are varchar(5000): a longer value
// fails the whole save, so the log stays well under it.
export const CHANGE_LOG_MAX_LENGTH = 4500;
const CHANGE_LOG_VALUE_MAX = 200;

const clipLogValue = (v) => {
  const s = v === undefined || v === null ? "" : String(v);
  return s.length > CHANGE_LOG_VALUE_MAX ? `${s.slice(0, CHANGE_LOG_VALUE_MAX - 3)}...` : s;
};

// The changed rows of a confirmed review as { field, from, to } with the names the
// cashier saw. Returns the JSON string, or null when nothing changed. Rows dropped
// to stay under CHANGE_LOG_MAX_LENGTH are counted in "omitted".
export const buildCustomerChangeLog = (review, { at, user } = {}) => {
  const rows = Array.isArray(review?.rows) ? review.rows.filter((r) => r && r.changed) : [];
  if (!rows.length) return null;
  const log = {
    type: review.mode === "create" ? "create" : "update",
    at: at || new Date().toISOString(),
    by: user || "",
    changes: rows.map((r) => ({ field: clipLogValue(r.label), from: clipLogValue(r.before), to: clipLogValue(r.after) })),
  };
  let json = JSON.stringify(log);
  let omitted = 0;
  while (json.length > CHANGE_LOG_MAX_LENGTH && log.changes.length) {
    log.changes.pop();
    omitted += 1;
    log.omitted = omitted;
    json = JSON.stringify(log);
  }
  return json;
};

// posOrderProcessor1 order metaData row for the change log kept on the cart customer
// (customer.customerChangeLog, set by createCustomer / updateCustomer). Null when the
// customer carries none -- every order whose customer was not saved from a form.
export const customerChangeLogMeta = (customer) => {
  const value = customer?.customerChangeLog;
  if (typeof value !== "string" || !value || value.length > CHANGE_LOG_MAX_LENGTH) return null;
  return { key: CHANGE_LOG_META_KEY, value };
};

// After a 200 from upsertB2CCustomer: read the B2B customer back by GSTIN and check
// the server holds the customer and both address ids that were just sent.
// fetchB2B = services getB2BCustomers. Never throws.
export const verifyB2BCustomerSaved = async ({ fetchB2B, gstin, sCustomerId, billingId, shippingId }) => {
  if (!gstin) return { status: "unknown", reason: "No GSTIN to look the customer up by." };
  try {
    const resp = await fetchB2B({ q: gstin, startRow: "0", endRow: "20" });
    const list = resp?.data?.getB2BCustomerList?.customers;
    if (!Array.isArray(list)) return { status: "unknown", reason: "Could not read the customer back from the server." };
    // The search already matched q against s_customer.taxid AND fin_lin_customer.gst_no,
    // so every result belongs to this GSTIN even when its gstNo field is empty.
    const wanted = gstin.toString().trim().toUpperCase();
    const sameGstin = list.filter((c) => serverGstin(c) === wanted);
    const match = sCustomerId ? list.find((c) => c.customerId === sCustomerId) : sameGstin[0] || (list.length === 1 ? list[0] : null);
    if (!match) {
      if (!list.length) return { status: "not_saved", reason: `No B2B customer with GSTIN ${gstin} was found on the server.` };
      if (sCustomerId && sameGstin.length) {
        return {
          status: "not_saved",
          reason: `The server holds GSTIN ${gstin} under a different B2B customer (${sameGstin.map((c) => c.customerId).join(", ")}), not the one just sent (${sCustomerId}).`,
        };
      }
      return { status: "unknown", reason: `The server returned customers for ${gstin}, but none could be matched to this save.` };
    }
    const rowIds = new Set([...(match.invoiceAddresses || []), ...(match.deliveryAddresses || [])].map((r) => r.s_customer_address_id));
    const missing = [];
    if (billingId && !rowIds.has(billingId)) missing.push("invoicing");
    if (shippingId && !rowIds.has(shippingId)) missing.push("delivery");
    if (missing.length) return { status: "not_saved", reason: `The ${missing.join(" and ")} address was not saved on the server.`, customer: match };
    return { status: "saved", customer: match };
  } catch (e) {
    return { status: "unknown", reason: e?.message || "Lookup failed." };
  }
};
